import React, { useMemo } from 'react'
import styled from 'styled-components'
import SlideBase from './SlideBase'
import WrappedImageBackground from '../WrappedImageBackground'
import Caption from 'LuxKit/Typography/Caption'
import AssistChip from 'LuxKit/Chips/AssistChip'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import Sheet from 'components/utils/Sheet'
import FadeBackground from '../FadeBackground'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import pluralize from 'lib/string/pluralize'
import Flag from 'LuxKit/Flag'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'

const ChipsContainer = styled(Sheet)`
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  mask-image: linear-gradient(to bottom, black 90%, transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, black 90%, transparent 100%);
`

const BottomContent = styled.div`
  flex-shrink: 0;
`

const NonInteractiveChip = styled(AssistChip)`
  pointer-events: none;
`

const BACKGROUND_FALLBACK_IMAGEID = 'wrap-travelled-backup'
const SLIDE_ID = 'destinations' as const

function getMemoriesHeading(isSingleCity: boolean, isMultiCity: boolean, isTotalsMode: boolean): string {
  if (isTotalsMode) return 'In 2025, Luxury Escapes travellers explored'
  if (isSingleCity) return 'You made memories in'
  if (isMultiCity) return 'You made memories across'
  return 'In 2025, Luxury Escapes travellers explored'
}

function DestinationsSlide() {
  // Use granular selectors to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const totalsData = useWrappedStorySelector(state => state.totalsData)
  const isPaused = useWrappedStorySelector(state => state.isPaused)
  const isCurrentSlide = useWrappedStorySelector(state => state.currentSlide?.id === SLIDE_ID)

  // Totals-only mode is determined by not having user wrapped data
  const isTotalsOnlyMode = !data
  const stats = data?.stats
  const citiesCount = stats?.citiesVisited.length ?? 0
  const countriesCount = stats?.countriesVisited ?? 0
  const firstCity = stats?.citiesVisited[0]
  const isSingleCity = citiesCount === 1
  const isMultiCity = !isSingleCity && !!citiesCount
  const notTravelled = !citiesCount
  // Use totals for fallback when user hasn't travelled or is in totals-only mode
  const useTotalsFallback = isTotalsOnlyMode || notTravelled
  const totalDestinationsSearched = totalsData?.stats.destinationsSearched ?? 0
  const backgroundImage = firstCity?.imageId ?? BACKGROUND_FALLBACK_IMAGEID
  const memoriesHeading = getMemoriesHeading(isSingleCity, isMultiCity, useTotalsFallback)
  const countryText = pluralize({ singular: 'country', plural: 'countries', count: countriesCount }).text
  const cityText = pluralize({ singular: 'city', plural: 'cities', count: citiesCount }).text

  // Memoize chips to prevent recreation
  const chipsOverlay = useMemo(() => {
    if (isSingleCity) return undefined
    return (
      <ChipsContainer paletteType="default">
        <Group direction="vertical" horizontalAlign="center" gap={8}>
          {stats?.citiesVisited.map((city, index) => (
            <NonInteractiveChip
              key={`${city.name}-${city.country}-${index}`}
              variant="filled"
              size="large"
            >
              <Flag countryCode={city.countryCode} /> {city.name}
            </NonInteractiveChip>
          ))}
        </Group>
      </ChipsContainer>
    )
  }, [isSingleCity, stats?.citiesVisited])

  // Memoize background to prevent recreation
  const background = useMemo(() => {
    if (!backgroundImage) return undefined
    return (
      <WrappedImageBackground
        imageId={backgroundImage}
        alt={firstCity?.name}
        kenBurnsVariant="left-to-right"
        isActive={isCurrentSlide && !isPaused}
      />
    )
  }, [backgroundImage, firstCity?.name, isCurrentSlide, isPaused])

  // Need either user data or totals data to render
  if (!data && !totalsData) return null

  return (
    <SlideBase
      verticalAlign={chipsOverlay ? 'space-between' : 'end'}
      background={background}
    >
      {chipsOverlay}
      <BottomContent>
        <FadeBackground>
          <Group direction="vertical" gap={12}>
            <Group direction="vertical" gap={4}>
              <Caption colour="highlight-primary" variant="small" format="uppercase">
                Destinations visited
              </Caption>
              <Heading variant="heading5">
                <b>{memoriesHeading}</b>
              </Heading>
            </Group>
            {isSingleCity && (
              <Heading variant="heading1">
                <i>{firstCity?.name}</i>
              </Heading>
            )}
            {isMultiCity && (
              <Heading variant="heading1">
                <i>
                  {citiesCount} {cityText}<br />
                  in {countriesCount} {countryText}
                </i>
              </Heading>
            )}
            {useTotalsFallback && (
              <Heading variant="heading1">
                <i>
                  <FormatDecimal value={totalDestinationsSearched} /> cities worldwide
                </i>
              </Heading>
            )}
          </Group>
        </FadeBackground>
      </BottomContent>
    </SlideBase>
  )
}

export default React.memo(DestinationsSlide)
